import { c as _c } from "react/compiler-runtime";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './primitives/alert-dialog';
import React, { ReactNode } from 'react';
import { Button } from './primitives/button';
import { Dialog, DialogContentWrapper, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from './primitives/dialog';
import { Loader2, Loader2Icon } from 'lucide-react';
export const DialogConfirmLoading = ({
  children,
  onConfirm,
  onCancel,
  description,
  title
}: React.PropsWithChildren<{
  onConfirm?: () => Promise<void>;
  onCancel?: () => void;
  description?: string;
  title?: string | ReactNode;
}>) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  return <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContentWrapper>
        <DialogHeader>
          <DialogTitle>{title ?? 'Are you sure?'}</DialogTitle>
          <DialogDescription>{description ?? 'This action cannot be undone.'}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose onClick={onCancel}>Cancel</DialogClose>
          <Button disabled={isLoading} onClick={async () => {
          if (isLoading) return;
          setIsLoading(true);
          try {
            await onConfirm?.();
            setOpen(false);
          } finally {
            setIsLoading(false);
          }
        }}>
            {isLoading && <Loader2Icon className="animate-spin" />}
            Continue
          </Button>
        </DialogFooter>
      </DialogContentWrapper>
    </Dialog>;
};
export const DialogConfirm = t0 => {
  const $ = _c(23);
  const {
    children,
    onConfirm,
    onCancel,
    description,
    title,
    confirmTestId
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <AlertDialogTrigger asChild={true}>{children}</AlertDialogTrigger>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = title ?? "Are you sure?";
  let t3;
  if ($[2] !== t2) {
    t3 = <AlertDialogTitle>{t2}</AlertDialogTitle>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = description ?? "This action cannot be undone.";
  let t5;
  if ($[4] !== t4) {
    t5 = <AlertDialogDescription>{t4}</AlertDialogDescription>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t3 || $[7] !== t5) {
    t6 = <AlertDialogHeader>{t3}{t5}</AlertDialogHeader>;
    $[6] = t3;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== onCancel) {
    t7 = <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>;
    $[9] = onCancel;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== confirmTestId || $[12] !== onConfirm) {
    t8 = <AlertDialogAction data-testid={confirmTestId} onClick={onConfirm}>Continue</AlertDialogAction>;
    $[11] = confirmTestId;
    $[12] = onConfirm;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <AlertDialogFooter>{t7}{t8}</AlertDialogFooter>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t9) {
    t10 = <AlertDialogContent>{t6}{t9}</AlertDialogContent>;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t1 || $[21] !== t10) {
    t11 = <AlertDialog>{t1}{t10}</AlertDialog>;
    $[20] = t1;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
};
DialogConfirm.displayName = 'DialogConfirm';
export function DialogConfirmationTemplate(props) {
  const $ = _c(25);
  const t0 = props.title ?? "Are you sure?";
  let t1;
  if ($[0] !== t0) {
    t1 = <DialogTitle>{t0}</DialogTitle>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = props.description ?? "This action cannot be undone";
  let t3;
  if ($[2] !== t2) {
    t3 = <div className="flex flex-col gap-2"><p>{t2}</p></div>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = props.cancelText ?? "Cancel";
  let t5;
  if ($[4] !== props.isLoading || $[5] !== props.onCancel || $[6] !== t4) {
    t5 = <Button variant="outline" onClick={props.onCancel} disabled={props.isLoading}>{t4}</Button>;
    $[4] = props.isLoading;
    $[5] = props.onCancel;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = props.acceptButtonVariant ?? "default";
  const t7 = props.confirmText ?? "Confirm";
  let t8;
  if ($[8] !== props.isLoading) {
    t8 = props.isLoading && <Loader2 className="size-4 animate-spin" />;
    $[8] = props.isLoading;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7 || $[11] !== t8) {
    t9 = <div className="flex items-center gap-2">{t7}{t8}</div>;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== props.isLoading || $[14] !== props.onConfirm || $[15] !== t6 || $[16] !== t9) {
    t10 = <Button variant={t6} onClick={props.onConfirm} disabled={props.isLoading}>{t9}</Button>;
    $[13] = props.isLoading;
    $[14] = props.onConfirm;
    $[15] = t6;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t5) {
    t11 = <DialogFooter>{t5}{t10}</DialogFooter>;
    $[18] = t10;
    $[19] = t5;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t1 || $[22] !== t11 || $[23] !== t3) {
    t12 = <>{t1}{t3}{t11}</>;
    $[21] = t1;
    $[22] = t11;
    $[23] = t3;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}